/**
 * Walking the organisation's folders.
 *
 * ITS OWN MODULE so it can be tested without a browser. The panel imports
 * `@/config`, which reads `window.location` and a build-time define as it
 * loads, so a test that touches the component needs a DOM and a bundler.
 * The same reason `vad.ts` and `align.ts` are separate from the voice
 * hook: the rules are the part worth testing.
 */

/** One level up, or null at the root. */
export function parentOf(path: string): string | null {
  const trimmed = path.replace(/\/+$/, '')
  if (trimmed === '' || trimmed === '/') return null
  const at = trimmed.lastIndexOf('/')
  return at <= 0 ? '/' : trimmed.slice(0, at)
}

/** Bytes as something a person reads. */
export function sizeLabel(n?: number): string {
  if (n == null) return ''
  if (n < 1024) return `${String(n)} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Where an upload lands: the folder being looked at, plus the filename.
 *
 * The server roots everything at `org-{orgId}` and strips traversal, but a
 * panel that composed `..` into a path would still be asking for it, and
 * the next person to read this should not have to go and check the server
 * to know that it does not.
 */
export function uploadPath(folder: string, filename: string): string {
  const base = folder.replace(/\/+$/, '')
  const safe = filename.replace(/[/\\]/g, '_').replace(/\.{2,}/g, '.')
  return `${base}/${safe}`
}

/**
 * What kind of file this is, from its name.
 *
 * Finder shows a Kind column and an icon per type, and both come from the
 * extension because that is all the listing gives us: the workspace route
 * returns name, type and size, with no modified date, so Kind takes the
 * column Finder would give to a date.
 */
export type FileKind =
  | 'folder'
  | 'image'
  | 'code'
  | 'data'
  | 'document'
  | 'text'
  | 'archive'
  | 'other'

const BY_EXTENSION: Record<string, FileKind> = {
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', svg: 'image',
  ts: 'code', tsx: 'code', js: 'code', jsx: 'code', mjs: 'code', html: 'code',
  css: 'code', py: 'code', sh: 'code', rb: 'code', go: 'code',
  json: 'data', csv: 'data', tsv: 'data', xlsx: 'data', xls: 'data', yaml: 'data', yml: 'data',
  pdf: 'document', docx: 'document', doc: 'document', pages: 'document',
  md: 'text', txt: 'text', log: 'text', env: 'text',
  zip: 'archive', gz: 'archive', tar: 'archive',
}

const KIND_LABEL: Record<FileKind, string> = {
  folder: 'Folder',
  image: 'Image',
  code: 'Code',
  data: 'Data',
  document: 'Document',
  text: 'Text',
  archive: 'Archive',
  other: 'File',
}

export function fileKind(name: string, isFolder: boolean): FileKind {
  if (isFolder) return 'folder'
  // A dotfile with no other extension IS its extension: `.env` is text.
  const parts = name.toLowerCase().split('.').filter(Boolean)
  const ext = parts.length > 1 ? parts[parts.length - 1] : parts[0] ?? ''
  return BY_EXTENSION[ext] ?? 'other'
}

export function kindLabel(kind: FileKind): string {
  return KIND_LABEL[kind]
}

/**
 * Finder's order: folders first, then files, each alphabetical and
 * case-insensitive.
 *
 * The route returns S3's order, which put `src` after six files in a
 * directory that is mostly configuration. Anybody looking for a folder
 * reads down the left edge, so folders belong at the top.
 */
export function finderOrder<T extends { name: string; type: string }>(
  items: readonly T[],
): T[] {
  return [...items].sort((a, b) => {
    const aFolder = a.type === 'directory'
    const bFolder = b.type === 'directory'
    if (aFolder !== bFolder) return aFolder ? -1 : 1
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  })
}

/** The clickable trail: `/apps/auth-2` → root, apps, auth-2. */
export function breadcrumb(path: string): { name: string; path: string }[] {
  const parts = path.split('/').filter(Boolean)
  const trail = [{ name: 'All files', path: '/' }]
  let at = ''
  for (const part of parts) {
    at += `/${part}`
    trail.push({ name: part, path: at })
  }
  return trail
}
